import Footer from "@/components/Footer";
import HBSwiper from "@/components/HBSwiper";
import { LinkPagination } from "@/components/LinkPagination";
import PinList from "@/components/PinList";
import TopicCard from "@/components/TopicCard";
import {
  BreadNav,
  Header,
  MoreTopicFooter,
  SEOHead,
} from "@/pageComponents/Zhuanti";
import styles from "@/pageComponents/Zhuanti/index.module.less";
import { getSearchPinsByQingzhou } from "@/services";
import { SearchPinResult } from "@/services/types";
import {
  getL1TopicResource,
  getRecommendTopicsByKeyword,
  getTopicStructure,
  isAlias,
  Pit,
  RecommendWord,
  TopicResource,
  TopicStructure,
} from "@/services/zhuanti";
import {
  getServerSideCurrentQuery,
  usePaginationCurrentQuery,
} from "@/utils/hooks/usePageQuery";
import { isResponseError } from "@/utils/request-util";
import { serverSideNotFound } from "@/utils/server/serverSide";
import { GetServerSideProps, GetServerSidePropsResult } from "next";

interface Props {
  level: 1 | 2 | 3;
  topic: TopicStructure;
  recommend: RecommendWord[];
  resource: TopicResource | null;
  pins: SearchPinResult | null;
}

const pageSize = 80;

export default function Index(props: Props) {
  return (
    <>
      <SEOHead topic={props.topic} />
      {props.level === 1 ? <L1Topic {...props} /> : <L2Topic {...props} />}
    </>
  );
}

export const getServerSideProps: GetServerSideProps<any> = async (context) => {
  try {
    const alias = context.params.alias as string;
    if (!isAlias(alias)) {
      return serverSideNotFound(context.res) as GetServerSidePropsResult<any>;
    }
    const topic = await getTopicStructure(alias);
    if (!topic || isResponseError(topic)) {
      return serverSideNotFound(context.res) as GetServerSidePropsResult<any>;
    }

    const level = getLevelByStructure(topic);
    let resource: TopicResource | null = null;
    if (level === 1) {
      resource = await getL1TopicResource(alias);
    }
    let pins = null;
    if (level !== 1) {
      const current = getServerSideCurrentQuery(context);
      pins = await getSearchPinsByQingzhou({
        text: topic.keyword,
        page: current,
        limit: pageSize,
      });
    }
    const recommend = await getRecommendTopicsByKeyword(topic.keyword);
    const props: Props = {
      topic,
      level,
      recommend: isResponseError(recommend) ? [] : recommend,
      resource: isResponseError(resource) ? null : resource,
      pins: isResponseError(pins) ? null : pins,
    };
    return {
      props,
    };
  } catch (err) {
    console.error(err);
    return serverSideNotFound(context.res) as GetServerSidePropsResult<any>;
  }
};

function getLevelByStructure(topic: TopicStructure): Props["level"] {
  let level: Props["level"] = 1;
  while (topic.parent) {
    level++;
    topic = topic.parent;
  }
  return Math.min(level, 3) as Props["level"];
}

function L1Topic(props: Props) {
  const { resource } = props;
  const ideaTitlePit = resource?.pits?.find((pit) =>
    pit.mark.endsWith("idea_title")
  );
  const ideaPit = resource?.pits?.find((pit) => pit.mark.endsWith("idea"));
  const calTitlePit = resource?.pits?.find((pit) =>
    pit.mark.endsWith("calendar_title")
  );
  const calPit = resource?.pits?.find((pit) => pit.mark.endsWith("calendar"));
  return (
    <div className={styles.container}>
      <BreadNav {...props} />
      <Header {...props} />
      <L1Resource titlePit={ideaTitlePit} pit={ideaPit} />
      <L1Resource titlePit={calTitlePit} pit={calPit} />
      <MoreTopicFooter {...props} />
      <Footer />
    </div>
  );
}

function L1Resource(props: { titlePit: Pit; pit: Pit }) {
  const { titlePit, pit } = props;
  if (!(titlePit && pit)) return null;

  const titleMaterial = titlePit?.materials?.[0]?.material;
  const ideaMaterial = pit?.materials;
  return (
    <div className={styles.resource}>
      {titleMaterial?.title ? (
        <>
          <div className={styles.title}>{titleMaterial.title}</div>
          <div className={styles.desc}>{titleMaterial?.description}</div>
        </>
      ) : null}
      {ideaMaterial ? (
        /* @ts-ignore */
        <HBSwiper
          className={styles.swiper}
          iconClassName={styles.resourceSwiperIcon}
        >
          {ideaMaterial.map((item) => {
            return (
              <TopicCard
                className={styles.topicCard}
                key={item.id}
                topic={{
                  name: item.title,
                  keyword_id: item.id,
                  url: item.material.link,
                  // 不能用 urlname
                  urlname: "",
                  cover: item.material.banner_img_url,
                  description: "",
                }}
              />
            );
          })}
        </HBSwiper>
      ) : null}
    </div>
  );
}

function L2Topic(props: Props) {
  return (
    <div className={styles.container}>
      <BreadNav {...props} />
      <Header {...props} />
      <L23PinList {...props} />
      <MoreTopicFooter {...props} />
      <Footer />
    </div>
  );
}

function L23PinList(props: Props) {
  const current = usePaginationCurrentQuery();
  const { pins } = props;
  return (
    <div className={styles.pinList}>
      <PinList
        dataSource={pins?.pins || []}
        hasMore={false}
        // @ts-ignore
        endMessage={null}
      />
      <LinkPagination
        className={styles.pagination}
        current={current}
        pageSize={pageSize}
        total={pins?.total || 0}
        showSizeChanger={false}
        hideOnSinglePage
      />
    </div>
  );
}
